import {
  Box,
  Button,
  Header,
  SpaceBetween,
  Container,
  FormField,
  Autosuggest,
  Flashbar,
  Select,
  Textarea,
} from '@amzn/awsui-components-console';
import { useState, useEffect } from 'react';

import { TraslatedDocumentStatus } from '../../../interfaces/index';
import {
  LaunchTask,
  WorkStatus,
  DocumentationStatus,
  UpdateWriterAssignmentCommand,
  UpdateWriterAssignmentCommandOutput,
  UpdateWriterAssignmentCommandInput,
  UpdateDocumentationNotesCommand,
  UpdateDocumentationNotesCommandOutput,
  UpdateDocumentationStatusCommand,
  UpdateDocumentationStatusCommandOutput,
} from '@amzn/awsdev-docs-virtual-smiley-conference-typescript-client';
import { OptionGroup } from '@amzn/awsui-components-console/internal/components/option/interfaces';
import { useAuthState } from '../../../authentication/context';
import { callApi } from '../../../client';
import { NoTokenError } from '../../../errors/NoTokenError';
import { useApiErrors } from '../../../components/ApiErrorContext';

interface LaunchTaskProps {
  task: LaunchTask;
}

const options = [
  {
    label: TraslatedDocumentStatus[DocumentationStatus.GREEN].toString(),
    value: DocumentationStatus.GREEN,
  },
  {
    label: TraslatedDocumentStatus[DocumentationStatus.YELLOW].toString(),
    value: DocumentationStatus.YELLOW,
  },
  {
    label: TraslatedDocumentStatus[DocumentationStatus.RED].toString(),
    value: DocumentationStatus.RED,
  },
];

export const EditWriterSection = ({ task }: LaunchTaskProps) => {
  const [editWriterEnabled, setEditWriterEnabled] = useState<boolean>(false);
  const [writerAlias, setWriterAlias] = useState<string | undefined>(
    task?.writerAssignment?.alias || undefined
  );
  const [writerAliasCache, setwriterAliasCache] = useState<string | undefined>(
    task?.writerAssignment?.alias || undefined
  );

  const [writerFullName, setWriterFullName] = useState<string | undefined>(
    task?.writerAssignment?.fullName || undefined
  );
  const [writerFullNameCache, setwriterFullNameCache] = useState<
    string | undefined
  >(task?.writerAssignment?.fullName || undefined);

  const [autoSuggestions, setAutoSuggestions] = useState<OptionGroup[]>([]);

  const { isAuthenticated, token } = useAuthState();
  const { pushError } = useApiErrors();

  const [errorIndicator, setErrorIndicator] = useState('');

  const [writerSuccess, setWriterSuccess] = useState(false);
  const [statusSuccess, setStatusSuccess] = useState(false);
  const [notesSuccess, setNotesSuccess] = useState(false);

  const [
    selectedDocumentationStatusString,
    setSelectedDocumentationStatusString,
  ] = useState({
    label: task?.documentationStatus
      ? TraslatedDocumentStatus[task?.documentationStatus].toString()
      : options[0].label,
    value: task?.documentationStatus
      ? task?.documentationStatus.toString()
      : options[0].value,
  });

  const [documentationNotes, setDocumentationNotes] = useState(
    task?.documentationNotes
  );

  useEffect(() => {
    setwriterAliasCache(task?.writerAssignment?.alias);
    setwriterFullNameCache(task?.writerAssignment?.fullName);

    setWriterAlias(task?.writerAssignment?.alias);
    setWriterFullName(task?.writerAssignment?.fullName);

    setSelectedDocumentationStatusString({
      label: task?.documentationStatus
        ? TraslatedDocumentStatus[task?.documentationStatus].toString()
        : options[0].label,
      value: task?.documentationStatus
        ? task?.documentationStatus.toString()
        : options[0].value,
    });
    setDocumentationNotes(task?.documentationNotes);
  }, [task]);

  async function makeApiCall(requestBody: UpdateWriterAssignmentCommandInput) {
    if (!token) throw new NoTokenError();
    try {
      const response: UpdateWriterAssignmentCommandOutput = await callApi(
        new UpdateWriterAssignmentCommand({
          launchId: requestBody.launchId,
          writerAssignment: requestBody.writerAssignment,
        }),
        token
      );
      if (response.$metadata.httpStatusCode == 200) {
        setWriterSuccess(true);
      }
    } catch (err: any) {
      pushError(
        'Update Assignee Failed',
        err.message || 'Failed to update assignee'
      );
    }
  }

  async function callUpdateDocumentationNotesAPI() {
    if (!token) throw new NoTokenError();
    try {
      const result: UpdateDocumentationNotesCommandOutput = await callApi(
        new UpdateDocumentationNotesCommand({
          launchId: task.launchId,
          documentationNotes: documentationNotes ?? '',
        }),
        token
      );
      if (result.$metadata.httpStatusCode == 200) {
        setNotesSuccess(true);
      }
    } catch (err: any) {
      pushError(
        'Update Notes Failed',
        err.message || 'Failed to update documentation notes'
      );
    }
  }

  async function callUpdateDocumentationStatusAPI() {
    if (!token) throw new NoTokenError();
    try {
      const result: UpdateDocumentationStatusCommandOutput = await callApi(
        new UpdateDocumentationStatusCommand({
          launchId: task.launchId,
          documentationStatus:
            DocumentationStatus[
              selectedDocumentationStatusString.value as keyof typeof DocumentationStatus
            ],
        }),
        token
      );
      if (result.$metadata.httpStatusCode == 200) {
        setStatusSuccess(true);
      }
    } catch (err: any) {
      pushError(
        'Update Status Failed',
        err.message || 'Failed to update documentation status'
      );
    }
  }

  useEffect(() => {
    if (writerAlias) {
      const allLetters = /^[A-Za-z0-9]+$/;
      const invalidChars = /[@#$%^&*]/;
      if (!allLetters.exec(writerAlias) || invalidChars.exec(writerAlias)) {
        setErrorIndicator('Alias Can only take letters');
      } else if (writerAlias.includes(' ')) {
        setErrorIndicator('Alias Cannot Have Space in Between');
      } else {
        setErrorIndicator('');
      }
    }
  }, [writerAlias]);

  return (
    <div>
      <SpaceBetween direction='vertical' size='l'>
        <SpaceBetween direction='vertical' size='xs'>
          {writerSuccess && (
            <Flashbar
              items={[
                {
                  header: 'Success',
                  type: 'success',
                  content: 'update assignee success',
                  dismissible: true,
                  dismissLabel: 'Dismiss',
                  onDismiss: () => setWriterSuccess(false),
                  id: 'writer-success',
                },
              ]}
            />
          )}
          {statusSuccess && (
            <Flashbar
              items={[
                {
                  header: 'Success',
                  type: 'success',
                  content: 'update documentation status success',
                  dismissible: true,
                  dismissLabel: 'Dismiss',
                  onDismiss: () => setStatusSuccess(false),
                  id: 'status-success',
                },
              ]}
            />
          )}
          {notesSuccess && (
            <Flashbar
              items={[
                {
                  header: 'Success',
                  type: 'success',
                  content: 'update documentation notes success',
                  dismissible: true,
                  dismissLabel: 'Dismiss',
                  onDismiss: () => setNotesSuccess(false),
                  id: 'notes-success',
                },
              ]}
            />
          )}
        </SpaceBetween>

        <Container
          header={
            <Header
              actions={
                <SpaceBetween direction='horizontal' size='m'>
                  <Button
                    variant='primary'
                    disabled={
                      !task?.allowedActions?.includes('WRITE') ||
                      errorIndicator.length > 0
                    }
                    onClick={() => {
                      editWriterEnabled ? setwriterAliasCache(writerAlias) : 0;
                      editWriterEnabled
                        ? setwriterFullNameCache(writerFullName)
                        : 0;
                      setEditWriterEnabled(editWriterEnabled ? false : true);

                      if (editWriterEnabled) {
                        const requestBody: UpdateWriterAssignmentCommandInput =
                          {
                            launchId: task.launchId,
                            writerAssignment: {
                              alias: writerAlias ?? '',
                            },
                          };
                        void makeApiCall(requestBody);
                      }
                    }}
                  >
                    {`${editWriterEnabled ? 'Save' : 'Edit'}`}
                  </Button>
                  <Box display={`${editWriterEnabled ? 'block' : 'none'}`}>
                    <Button
                      onClick={() => {
                        setEditWriterEnabled(false);
                        setWriterAlias(writerAliasCache);
                        setWriterFullName(writerFullNameCache);
                        setErrorIndicator('');
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </SpaceBetween>
              }
            >
              Assignee
            </Header>
          }
        >
          <FormField label='Assignee Alias' errorText={errorIndicator}>
            <Autosuggest
              readOnly={!editWriterEnabled}
              disabled={!editWriterEnabled}
              onChange={({ detail }) => {
                setWriterAlias(detail.value);
                detail.value ? 0 : setErrorIndicator('field cannot be empty!');
              }}
              value={writerAlias ? writerAlias : ''}
              options={autoSuggestions}
              loadingText='Loading alias'
              empty='No matches found'
              virtualScroll
            />
          </FormField>
        </Container>
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='Documentation status'></FormField>
            <Select
              selectedOption={selectedDocumentationStatusString}
              onChange={({ detail }) => {
                setSelectedDocumentationStatusString({
                  label: detail.selectedOption.label!,
                  value: detail.selectedOption.value!,
                });
              }}
              options={options}
            />
            <SpaceBetween direction='horizontal' size='l'>
              <Button
                variant='primary'
                disabled={task?.workStatus === WorkStatus.DELETED}
                onClick={() => callUpdateDocumentationStatusAPI()}
              >
                Save Status
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
        <Container>
          <SpaceBetween direction='vertical' size='l'>
            <FormField label='Documentation Notes'></FormField>
            <Textarea
              value={documentationNotes ?? ''}
              onChange={({ detail }) => {
                setDocumentationNotes(detail.value);
              }}
            />
            <SpaceBetween direction='horizontal' size='l'>
              <Button
                variant='primary'
                disabled={task?.workStatus === WorkStatus.DELETED}
                onClick={() => callUpdateDocumentationNotesAPI()}
              >
                Save Notes
              </Button>
            </SpaceBetween>
          </SpaceBetween>
        </Container>
      </SpaceBetween>
    </div>
  );
};

export default EditWriterSection;
